import {
  I2cRadioButton,
  I2cCheckbox,
  I2cCheckboxGroup,
  I2cRadioGroup,
  I2cListItem,
  I2cInput,
} from "@webcomponents/i2cwebcomponents/dist/react";
import "../../PrepaidComponents/Prepaid.scss";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup"; 
import ErrorMessage from "../../../Components/SharedComponent/ErrorMessage/ErrorMessage";
import { validationSchemaCardHolderWebsite } from "./ValidationSchema";
import { agentWebsiteLang, webRequire } from "./stringUtils"; 
 
const CardHolderWebsite = ({
  setAddFormData,
  addFormData,
  setFormValidStates,
  formDataJson,
  isFetching,
}) => {
  // Initialize React Hook Form with Yup resolver
  const methods = useForm({
    resolver: yupResolver(validationSchemaCardHolderWebsite),
    mode: "all",
    reValidateMode: "all",
    shouldUseNativeValidation: false,
    criteriaMode: "all",
    defaultValues: {
      cardholder_website_would_you_like_cardholder_website: "no",
      cardholder_website_web_language: [],
      cardholder_website_web_language_other: "",
      cardholder_website_url: "",
    },
  });

  const {
    formState: { errors },
  } = methods;

  const fields = [
    "cardholder_website_would_you_like_cardholder_website",
    "cardholder_website_web_language",
    "cardholder_website_web_language_other",
    "cardholder_website_url",
  ];

  // Handle value changes and update parent state
  const handleValueChanges = React.useCallback(() => {
    const values = methods.getValues();
    setAddFormData({
      ...addFormData,
      3: {
        ...addFormData["3"],
        "3_3": { ...values },
      },
    });
  }, [methods, setAddFormData, addFormData]);

  // Load existing form data
  useEffect(() => {
    if (!isFetching && Object.keys(formDataJson ?? {}).length) {
      fields.forEach((field) => {
        const value = formDataJson[3]?.["3_3"]?.[field];
        if (value !== undefined && value !== null) {
          methods.setValue(field, value);
        }
      });
      methods.trigger();
      handleValueChanges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching]);

  // Trigger validation when component mounts
  useEffect(() => {
    methods.trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update form validation state when errors change
  useEffect(() => {
    const errorsLength = Object.keys(errors)?.length;
    if (errorsLength === 0) {
      setFormValidStates("3_3", true);
    } else {
      setFormValidStates("3_3", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const isWebRequired = methods.watch("cardholder_website_would_you_like_cardholder_website") === "yes";

  // Handle checkbox group change
  const handleWebLangAdd = (lang) => {
    let updated = [...methods.getValues("cardholder_website_web_language")];
    if (updated.includes(lang)) {
      updated = updated.filter((l) => l !== lang);
      if (lang === "other") {
        methods.setValue("cardholder_website_web_language_other", "");
      }
    } else {
      updated.push(lang);
    }
    methods.setValue("cardholder_website_web_language", updated);
    methods.trigger();
    handleValueChanges();
  };

  return (
    <React.Fragment>
      <div className="forms-instance">
        <div i2c-grid="grid gap-md">
          <div i2c-grid="12">
            <div className="sub-section">
              <div className="sub-heading mb-16">
                Would you like Cardholder Website?
              </div>
              <I2cRadioGroup
                label=""
                value={methods.watch("cardholder_website_would_you_like_cardholder_website")}
                name="cardholder_website_would_you_like_cardholder_website"
                onI2cChange={(e) => {
                  const value = String(e.target.value);
                  methods.setValue("cardholder_website_would_you_like_cardholder_website", value);

                  if (value !== "yes") {
                    methods.setValue("cardholder_website_web_language", []);
                    methods.setValue("cardholder_website_web_language_other", "");
                    methods.setValue("cardholder_website_url", "");
                  }

                  methods.trigger();
                  handleValueChanges();
                }}
              >
                {webRequire.map((option) => (
                  <I2cRadioButton key={option.value} value={option.value}>
                    {option.label}
                  </I2cRadioButton>
                ))}
              </I2cRadioGroup>
              {errors.cardholder_website_would_you_like_cardholder_website && (
                <ErrorMessage message={errors.cardholder_website_would_you_like_cardholder_website.message} />
              )}
            </div>
            {isWebRequired && (
              <>
                <div className="sub-section">
                  <div className="sub-heading mb-16">C-Holder Web Language</div>
                  <I2cCheckboxGroup
                    key={`cardholder-language-${methods.watch("cardholder_website_would_you_like_cardholder_website")}`}
                  >
                    <div i2c-grid="grid gap-sm">
                      {agentWebsiteLang.map((web, index) => (
                        <I2cListItem
                          key={index}
                          i2c-grid="4@md"
                          variant="receipt"
                          label={web.label}
                          value=""
                          type="checkbox"
                          checked={methods.watch("cardholder_website_web_language").includes(web.value)}
                          onI2cCheckboxClicked={() =>
                            handleWebLangAdd(web.value)
                          }
                        >
                          <I2cCheckbox
                            slot="prefix"
                            name="ic_cAgentLang"
                            value={web.value}
                            label=""
                            filled
                            checked={methods.watch("cardholder_website_web_language").includes(web.value)}
                            className="customCheckbox"
                          ></I2cCheckbox>
                        </I2cListItem>
                      ))}
                    </div>
                  </I2cCheckboxGroup>
                  {errors.cardholder_website_web_language && (
                    <ErrorMessage message={errors.cardholder_website_web_language.message} />
                  )}
                  {methods
                    .watch("cardholder_website_web_language")
                    .includes("other") && (
                    <div i2c-grid="grid gap-sm" className="mt-16">
                      <div i2c-grid="4@md">
                        <div className="single-field-wrapper">
                          <I2cInput
                            className="single-input-field"
                            placeholder="Please Specify"
                            size="large"
                            label="Enter Details"
                            name="cardholder_website_web_language_other"
                            value={methods.watch(
                              "cardholder_website_web_language_other",
                            )}
                            onI2cInput={(e) => {
                              const value = e.target.value;
                              methods.setValue(
                                "cardholder_website_web_language_other",
                                value,
                              );
                              methods.trigger();
                              handleValueChanges();
                            }}
                            invalid={
                              errors.cardholder_website_web_language_other
                                ? true
                                : false
                            }
                          />
                          {errors.cardholder_website_web_language_other && (
                            <ErrorMessage
                              message={
                                errors.cardholder_website_web_language_other
                                  .message
                              }
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div> 
                <div i2c-grid="grid gap-sm" className="mt-16">
                  <div i2c-grid="4@md">
                    <div className="single-field-wrapper">
                      <I2cInput
                        label="Cardholder Website URL"
                        className="single-input-field"
                        type="text"
                        onI2cInput={(e) => {
                          const value = e.target.value;
                          methods.setValue("cardholder_website_url", value, {
                            shouldTouch: true,
                            shouldValidate: true,
                          });
                          handleValueChanges();
                        }}
                        value={methods.watch("cardholder_website_url")}
                        placeholder="www.cholder.com"
                        name="cardholder_website_url"
                        size="large"
                        invalid={errors.cardholder_website_url ? true : false}
                      />
                      <ErrorMessage
                        show={
                          errors.cardholder_website_url &&
                          methods.formState.touchedFields.cardholder_website_url
                            ? true
                            : false
                        }
                        message={errors.cardholder_website_url?.message ?? "Invalid URL"}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default CardHolderWebsite;
